import { ApiError, RequestAbortError } from '../../api/client.js';
import { h, toast, confirmModal, formModal, type View } from '../components/dom.js';
import { renderHeader } from '../components/header.js';
import { createInstallAppCard } from '../../components/install-app.js';
import { createPerceptionPanel } from '../components/perception-panel.js';
import type { SessionController } from '../../session/controller.js';
import { getMobileBridge } from '../../platform/mobile-bridge.js';

export function createPerfilView(controller: SessionController): View {
  let active = true;
  const isNativeMobile = getMobileBridge() !== null;
  const root = h('section', { class: 'view view-perfil', id: 'view-perfil' });
  root.appendChild(renderHeader('perfil', { subtitle: 'Tu cuenta y cómo te gusta conversar.' }));
  const body = h('div', { class: 'section-body' });
  root.appendChild(body);
  const refreshButton = h('button', { class: 'btn ghost refresh', type: 'button', hidden: true }, ['Reintentar']);
  refreshButton.addEventListener('click', () => { loaded = false; void load(); });
  root.querySelector('.topbar-right')?.append(refreshButton);

  const api = controller.getApi();
  const user = controller.getUser();
  const email = h('div', { class: 'kv-row' }, [h('dt', {}, ['Correo']), h('dd', {}, [user?.email ?? user?.username ?? '—'])]);
  const userId = h('div', { class: 'kv-row' }, [h('dt', {}, ['ID']), h('dd', {}, [user?.id ?? '—'])]);

  // Identity card
  const idCard = h('section', { class: 'card' });
  idCard.append(h('h2', { class: 'card-title' }, ['Tu cuenta de ASIS']));
  const kv = h('dl', { class: 'kv' });
  kv.append(email);
  idCard.appendChild(kv);
  const logout = h('button', {class:'btn ghost', type:'button'}, ['Cerrar sesión']);
  logout.addEventListener('click', () => {
    if (!active || logout.disabled) return;
    logout.disabled = true;
    void controller.logout().catch(err => { if (active) { logout.disabled = false; toast(apiMessage(err), 'error'); } });
  });
  idCard.append(h('p', {class:'card-sub'}, ['Tu cuenta comparte dispositivos, grupos y conocimiento entre el Mac y el móvil.']), logout);
  body.appendChild(idCard);
  const installation = createInstallAppCard();
  const advanced = h('details', { class: 'secondary-details account-advanced' }, [h('summary', {}, ['Datos y opciones avanzadas']), h('dl', { class: 'kv' }, [userId]), installation.el]);

  // Preferences card
  const prefCard = h('section', { class: 'card' });
  prefCard.append(h('h2', { class: 'card-title' }, ['Preferencias']));
  const form = h('form', { class: 'form-stack' });
  const dnLabel = h('label', { class: 'field' }, []);
  dnLabel.append(h('span', {}, ['Nombre visible']));
  const dnInput = h('input', { class: 'input', type: 'text', maxlength: '60' });
  dnLabel.appendChild(dnInput);
  const langLabel = h('label', { class: 'field' }, []);
  langLabel.append(h('span', {}, ['Idioma']));
  const langSelect = h('select', { class: 'select' });
  langSelect.append(h('option', { value: 'es' }, ['Español (es)']));
  langLabel.appendChild(langSelect);
  const memLabel = h('label', { class: 'field inline' }, []);
  const memChk = h('input', { type: 'checkbox' });
  memLabel.append(memChk, document.createTextNode(' Usar mis recuerdos al conversar'));
  const saveBtn = h('button', { type: 'submit', class: 'btn primary' }, ['Guardar cambios']);
  form.append(dnLabel, langLabel, memLabel, saveBtn);
  prefCard.appendChild(form);
  body.appendChild(prefCard);

  // Data card
  const dataCard = h('section', { class: 'card' });
  dataCard.append(h('h2', { class: 'card-title' }, ['Tus datos']));
  const dataBody = h('p', { class: 'card-sub' }, ['Exporta tus datos del servidor, sin claves. El borrado personal elimina tus perfiles, recuerdos, historial, conectores, credenciales y configuración de modelos, además de tus aportaciones propias; también te retira de grupos ajenos. Conserva tu cuenta, dispositivos y grupos que administras, junto con los datos de otras personas. La personalización local se gestiona en Personas.']);
  const actions = h('div', { class: 'form-row' });
  const exportBtn = h('button', { type: 'button', class: 'btn ghost' }, ['Exportar datos del servidor']);
  const deleteBtn = h('button', { type: 'button', class: 'btn ghost danger' }, ['Borrar datos del servidor']);
  actions.append(exportBtn, deleteBtn);
  dataCard.append(dataBody, actions);
  advanced.appendChild(dataCard);

  let perception = isNativeMobile ? null : createPerceptionPanel(window.asisDesktop?.local);
  const legacyWrap = h('details', {class:'card legacy-perception'}, []);
  const legacySummary = h('summary', {}, ['Personalización anterior de este Mac']);
  const legacyNote = h('p', {class:'card-sub'}, ['Estas muestras pertenecen al perfil anterior de la cuenta. Para registrar o probar una persona concreta, abre Personas. El borrado de este panel solo afecta a esas muestras anteriores.']);
  if (perception) legacyWrap.append(legacySummary, legacyNote, perception.el);

  const templatesCard = h('section', { class: 'card account-templates' });
  templatesCard.append(h('h2', { class: 'card-title' }, ['Plantillas biométricas locales de esta cuenta']));
  const templatesSub = h('p', {class:'card-sub'}, ['Elimina las plantillas de cara y voz de tu cuenta en este Mac, incluidas las de perfiles antiguos o ya borrados. Se conservan los modelos, los datos del servidor y las plantillas de otras cuentas.']);
  const templatesBtn = h('button', { type: 'button', class: 'btn ghost danger' }, ['Borrar todas las plantillas de este Mac']);
  const templatesHint = h('p', { class: 'card-sub', role: 'status', 'aria-live': 'polite' }, []);
  templatesCard.append(templatesSub, templatesBtn, templatesHint);
  if (!isNativeMobile) advanced.append(templatesCard);

  type IdentityBridge = { removeAccountBiometrics?: (args: { confirm: true }) => Promise<unknown> };
  const accountBridge: IdentityBridge | null = (() => {
    if (isNativeMobile) return null;
    const candidate = window.asisDesktop?.local as IdentityBridge | undefined;
    if (!candidate) return null;
    return typeof candidate.removeAccountBiometrics === 'function' ? candidate : null;
  })();
  if (!accountBridge) {
    templatesBtn.disabled = true;
    templatesBtn.title = 'Actualiza la aplicación de escritorio para borrar todas las plantillas de esta cuenta.';
    templatesHint.textContent = window.asisDesktop ? 'Actualiza ASIS para borrar todas las plantillas de esta cuenta.' : 'Disponible en la aplicación de ASIS para Mac. Abre Mi cuenta allí para gestionar las plantillas de ese equipo.';
  }

  let templatesPending = false;
  function refreshLegacyPanel(): void {
    const previous = perception;
    if (!previous || isNativeMobile) return;
    previous.destroy?.();
    perception = createPerceptionPanel(window.asisDesktop?.local);
    legacyWrap.replaceChild(perception.el, previous.el);
  }
  templatesBtn.addEventListener('click', async () => {
    if (!active || templatesPending || !accountBridge?.removeAccountBiometrics) return;
    templatesPending = true;
    templatesBtn.disabled = true;
    try {
      const confirmed = await confirmModal({
        title: 'Borrar todas las plantillas de este Mac',
        body: `Se borrarán las plantillas de cara y voz de ${user?.email ?? user?.username ?? 'tu cuenta actual'} en ESTE Mac, incluidas las de perfiles eliminados. Se conservarán modelos, datos del servidor y otras cuentas.`,
        confirmLabel: 'Borrar plantillas', danger: true,
      });
      if (!active || !confirmed) return;
      templatesHint.textContent = 'Borrando plantillas…';
      await accountBridge.removeAccountBiometrics({confirm:true});
      if (!active) return;
      templatesHint.textContent = 'Todas las plantillas de esta cuenta se han borrado de este Mac.';
      refreshLegacyPanel();
    } catch (err) {
      if (active) {
        templatesHint.textContent = `No se pudo completar la operación: ${apiMessage(err)}`;
        toast(templatesHint.textContent, 'error');
      }
    } finally {
      templatesPending = false;
      if (active) templatesBtn.disabled = !accountBridge?.removeAccountBiometrics;
    }
  });

  if (!isNativeMobile) advanced.append(legacyWrap);
  body.append(advanced);

  // Load
  let loaded = false;
  saveBtn.disabled = true;
  async function load(): Promise<void> {
    if (loaded) return;
    loaded = true;
    try {
      const p = await api.getProfile();
      if (!active) return;
      refreshButton.hidden = true;
      dnInput.value = p.displayName;
      langSelect.value = p.language;
      memChk.checked = p.memoryEnabled;
      saveBtn.disabled = false;
    } catch (err) {
      if (!active || err instanceof RequestAbortError) return;
      loaded = false;
      refreshButton.hidden = false;
      toast(`No se pudo cargar el perfil: ${apiMessage(err)}`, 'error');
    }
  }

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (saveBtn.disabled) return;
    saveBtn.disabled = true;
    const body = {
      displayName: dnInput.value.trim() || (user?.displayName ?? ''),
      language: 'es' as const,
      memoryEnabled: memChk.checked,
    };
    try {
      await api.patchProfile(body);
      if (!active) return;
      controller.updateDisplayName(body.displayName);
      toast('Perfil guardado', 'ok');
    } catch (err) {
      if (!active || err instanceof RequestAbortError) return;
      toast(`No se pudo guardar: ${apiMessage(err)}`, 'error');
    } finally { saveBtn.disabled = false; }
  });

  exportBtn.addEventListener('click', async () => {
    exportBtn.disabled = true;
    try {
      const data = await api.exportData();
      if (!active) return;
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = h('a', { href: url, download: `asis-export-${new Date().toISOString().slice(0, 10)}.json` });
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast('Exportación lista', 'ok');
    } catch (err) {
      if (!active || err instanceof RequestAbortError) return;
      toast(`No se pudo exportar: ${apiMessage(err)}`, 'error');
    } finally { exportBtn.disabled = false; }
  });

  deleteBtn.addEventListener('click', async () => {
    await formModal({ title: 'Borrar datos del servidor', body: 'Se borrarán tus perfiles, datos privados, recuerdos, historial, conectores, credenciales y configuración de modelos, además de tus aportaciones propias, y saldrás de grupos ajenos. Se conservan tu cuenta, dispositivos, grupos que administras y datos de otras personas. Para borrar un grupo completo usa Grupos. Las plantillas biométricas locales se conservan y se borran por separado en cada dispositivo. Escribe BORRAR para confirmar.', submitLabel: 'Borrar datos', danger: true, fields: [{ name: 'confirm', label: 'Confirmación' }], onSave: async values => {
      if (values.confirm !== 'BORRAR') throw new Error('Escribe BORRAR exactamente para confirmar.');
      if (!active) throw new Error('La vista se cerró.');
      await api.deleteData();
      if (!active) return;
      if (active) toast('Datos del servidor borrados', 'ok');
    } });
  });

  void load();
  return {
    el: root,
    destroy() {
      active = false;
      templatesPending = false;
      templatesBtn.disabled = true;
      perception?.destroy?.();
      installation.destroy?.();
    },
    update() { void load(); },
  };
}

function apiMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return (err as Error).message ?? 'error';
}
